import { Input, cn } from "@getgreenline/blaze-ui";
import { useFormikContext } from "formik";
import { useDeferredValue } from "react";
import { type Brand, useBrandsCatalog } from "@/hooks/use-brands-catalog";
import { type CampaignFormValues } from "../campaign-form";
import { BrandCard } from "../cards/brand-card";
import { FormTip } from "../../shared/form-tip";
import { StepAlert } from "../../shared/step-alert";
import { StepHeader } from "../../shared/step-header";

type BrandStepProps = {
  errorMessage?: string;
  errorField?: string;
  showError?: boolean;
  showLoadingSelectedBrand?: boolean;
  onDismissError: () => void;
};

type BrandSearchResultsProps = {
  brands: Brand[];
  error: unknown;
  hasSearchQuery: boolean;
  isLoading: boolean;
  searchQuery: string;
  selectedBrandId: string;
  showBrandIdError: boolean;
  onSelectBrand: (brand: Brand) => void;
};

function BrandSearchResults({
  brands,
  error,
  hasSearchQuery,
  isLoading,
  searchQuery,
  selectedBrandId,
  showBrandIdError,
  onSelectBrand,
}: BrandSearchResultsProps) {
  if (!hasSearchQuery) {
    return (
      <p className="tw:mt-6 tw:text-sm tw:text-muted-foreground">
        Search by brand name to view available brands.
      </p>
    );
  }

  if (isLoading) {
    return (
      <p className="tw:mt-8 tw:text-lg tw:text-muted-foreground">
        Loading brands...
      </p>
    );
  }

  if (error) {
    return (
      <p className="tw:mt-8 tw:text-lg tw:text-destructive">
        Unable to load brands.
      </p>
    );
  }

  if (brands.length === 0) {
    return (
      <p className="tw:mt-8 tw:text-lg tw:text-muted-foreground">
        No brands found for &quot;{searchQuery.trim()}&quot;.
      </p>
    );
  }

  return (
    <div
      className={cn(
        "tw:mt-8 tw:grid tw:grid-cols-2 tw:gap-4",
        showBrandIdError &&
          "tw:rounded-xl tw:border tw:border-destructive/40 tw:bg-destructive/5 tw:p-3",
      )}
    >
      {brands.map((brand) => (
        <BrandCard
          key={brand.id}
          {...brand}
          selected={brand.id === selectedBrandId}
          onClick={() => onSelectBrand(brand)}
        />
      ))}
    </div>
  );
}

export function BrandStep({
  errorMessage,
  errorField,
  showError = false,
  showLoadingSelectedBrand = false,
  onDismissError,
}: BrandStepProps) {
  const { values, setFieldValue } = useFormikContext<CampaignFormValues>();
  const deferredSearchQuery = useDeferredValue(values.brandSearchQuery);
  const hasSearchQuery = deferredSearchQuery.trim().length > 0;
  const {
    data: brands = [],
    isLoading,
    error,
  } = useBrandsCatalog(deferredSearchQuery, {
    enabled: hasSearchQuery,
  });

  return (
    <section className="tw:max-w-4xl">
      <StepHeader
        title="Which brand are you promoting?"
        description="Your brand filters the product catalog and pre-fills campaign assets. All brands must be pre-approved and licensed in their target markets."
      />
      <StepAlert
        show={showError}
        message={errorMessage}
        onDismiss={onDismissError}
      />

      {(values.brandId || showLoadingSelectedBrand) && (
        <div>
          <h2>Selected brand:</h2>
          <BrandCard
            name={values.brand?.name ?? "Loading brand name..."}
            description={
              values.brand?.description ?? "Loading brand description..."
            }
            avatarImageUrl={values.brand?.avatarImageUrl}
          ></BrandCard>
        </div>
      )}

      <div className="tw:mt-8 tw:max-w-xl">
        <Input
          value={values.brandSearchQuery}
          onChange={(event) =>
            setFieldValue("brandSearchQuery", event.target.value)
          }
          placeholder="Search brands"
          aria-label="Search brands"
        />
      </div>

      <BrandSearchResults
        brands={brands}
        error={error}
        hasSearchQuery={hasSearchQuery}
        isLoading={isLoading}
        searchQuery={deferredSearchQuery}
        selectedBrandId={values.brandId}
        showBrandIdError={showError && errorField === "brandId"}
        onSelectBrand={(brand) => {
          setFieldValue("brandId", brand.id);
          setFieldValue("brand", brand);
        }}
      />

      <FormTip className="tw:mt-4">
        Don&apos;t see your brand? Contact your Blaze Ads account manager.
      </FormTip>
    </section>
  );
}
